import type { AxiosInstance } from 'axios';
import {
  isSealedPayload,
  PAYLOAD_HKDF_INFO,
  type CryptoHandshakeResponseDto,
  type SealedPayload,
} from '@fms/contracts';

export { isSealedPayload };
export const PAYLOAD_KID_HEADER = 'x-payload-kid';

/** Base64 (standard alphabet) ⇄ bytes without Buffer — browser + Node ≥ 18. */
function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Browser side of the transport payload encryption (see the API's
 * middleware/payload-crypto.ts and modules/crypto for the server half).
 *
 * ECDH (P-256) handshake → HKDF-SHA256 → AES-256-GCM session key. The private
 * key is generated non-extractable and never leaves WebCrypto; the server
 * returns the derived key wrapped as an opaque `kid` which is echoed in the
 * `x-payload-kid` header so the server stays stateless.
 *
 * Local development keeps PAYLOAD_ENCRYPTION=false server-side, so this class
 * stays dormant and every payload remains inspectable in the network tab.
 */
export class PayloadCipher {
  private key: CryptoKey | null = null;
  private kid: string | null = null;
  private expiresAtMs = 0;
  private handshakePromise: Promise<void> | null = null;

  /** Sealing engages when true — flipped by ctor option or the first 428. */
  active: boolean;

  constructor(
    private readonly http: AxiosInstance,
    active = false,
  ) {
    this.active = active;
  }

  activate(): void {
    this.active = true;
  }

  /** Drop the current key (e.g. after the server reports it expired). */
  invalidate(): void {
    this.key = null;
    this.kid = null;
    this.expiresAtMs = 0;
  }

  /** Single-flight handshake; refreshes 30 s before the kid expires. */
  async ensureKey(): Promise<string> {
    if (this.key && this.kid && Date.now() < this.expiresAtMs - 30_000) return this.kid;
    this.handshakePromise ??= this.doHandshake().finally(() => {
      this.handshakePromise = null;
    });
    await this.handshakePromise;
    return this.kid!;
  }

  private async doHandshake(): Promise<void> {
    const subtle = globalThis.crypto.subtle;
    const pair = await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, [
      'deriveBits',
    ]);
    const publicJwk = await subtle.exportKey('jwk', pair.publicKey);

    const { data } = await this.http.post<CryptoHandshakeResponseDto>('/crypto/handshake', {
      clientPublicJwk: { kty: 'EC', crv: 'P-256', x: publicJwk.x, y: publicJwk.y },
    });

    const serverKey = await subtle.importKey(
      'jwk',
      data.serverPublicJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
    const shared = await subtle.deriveBits(
      { name: 'ECDH', public: serverKey },
      pair.privateKey,
      256,
    );
    const hkdfKey = await subtle.importKey('raw', shared, 'HKDF', false, ['deriveBits']);
    const keyBits = await subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: b64ToBytes(data.salt),
        info: new TextEncoder().encode(PAYLOAD_HKDF_INFO),
      },
      hkdfKey,
      256,
    );
    this.key = await subtle.importKey('raw', keyBits, { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);
    this.kid = data.kid;
    this.expiresAtMs = Date.now() + data.expiresInSec * 1000;
  }

  async seal(value: unknown): Promise<SealedPayload> {
    if (!this.key) await this.ensureKey();
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const encrypted = new Uint8Array(
      await globalThis.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        this.key!,
        new TextEncoder().encode(JSON.stringify(value)),
      ),
    );
    // WebCrypto appends the 16-byte tag to the ciphertext; the wire format
    // (and node:crypto on the server) keeps them separate.
    const ct = encrypted.subarray(0, encrypted.length - 16);
    const tag = encrypted.subarray(encrypted.length - 16);
    return { __sealed: { iv: bytesToB64(iv), ct: bytesToB64(ct), tag: bytesToB64(tag) } };
  }

  async unseal(sealed: SealedPayload): Promise<unknown> {
    if (!this.key) throw new Error('No payload key established');
    const iv = b64ToBytes(sealed.__sealed.iv);
    const ct = b64ToBytes(sealed.__sealed.ct);
    const tag = b64ToBytes(sealed.__sealed.tag);
    const joined = new Uint8Array(ct.length + tag.length);
    joined.set(ct);
    joined.set(tag, ct.length);
    const plain = await globalThis.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, this.key, joined);
    return JSON.parse(new TextDecoder().decode(plain));
  }
}
